import { Link } from "react-router-dom";
import { ArrowLeft, Target, TrendingUp, Zap, Star } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSupabase } from "@/hooks/useSupabase";
import { Attempt, Athlete } from "@shared/types";
import { ROUTES } from "@/lib/store";

interface RoutePerformanceMetrics {
  routeNumber: number;
  totalAttempts: number;
  uniqueAthletes: number;
  totalAthletes: number;
  successfulCompletions: number;
  flashCompletions: number;
  percentAttempted: number;
  percentCompleted: number;
  successRate: number;
  flashRate: number;
  averageAttemptsToComplete: number;
  averageScoreCompleted: number;
  averageRankingCompleted: number;
  flashPoints: number;
  secondPlusPoints: number;
  difficulty: 'Fácil' | 'Média' | 'Difícil' | 'Ninguém fez';
}

export default function RoutePerformance() {
  const { store } = useSupabase();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [routeMetrics, setRouteMetrics] = useState<RoutePerformanceMetrics[]>([]);
  const [sortBy, setSortBy] = useState<string>("routeNumber");

  useEffect(() => {
    loadRoutePerformanceData();
  }, []);

  const loadRoutePerformanceData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [allAttempts, allAthletes] = await Promise.all([
        store.getAttempts(),
        store.getAthletes(),
      ]);

      if (!allAttempts || !allAthletes) {
        throw new Error("Falha ao carregar dados");
      }

      const metrics = calculateRouteMetrics(allAttempts, allAthletes);
      setRouteMetrics(metrics);
    } catch (error) {
      console.error("Error loading route performance data:", error);
      setError("Falha ao carregar dados de performance. Tente novamente mais tarde.");
    } finally {
      setLoading(false);
    }
  };

  const calculateRouteMetrics = (
    attempts: Attempt[],
    athletes: Athlete[]
  ): RoutePerformanceMetrics[] => {
    const metrics: RoutePerformanceMetrics[] = [];

    // First, calculate all athlete rankings for average ranking calculation
    const athleteRankings = new Map<string, number>();
    const totalAthletes = athletes.length;

    ['Masculino', 'Feminino'].forEach(gender => {
      const genderAthletes = athletes.filter(a => a.gender === gender);
      const genderRanking = genderAthletes.map(athlete => {
        const athleteAttempts = attempts.filter(a => a.athleteId === athlete.id);
        const routeScores = new Map<number, { points: number; routeNumber: number }>();

        athleteAttempts.forEach((attempt) => {
          if (attempt.result === "completed") {
            const existing = routeScores.get(attempt.routeNumber);
            if (!existing || attempt.points > existing.points) {
              routeScores.set(attempt.routeNumber, {
                points: attempt.points,
                routeNumber: attempt.routeNumber,
              });
            }
          }
        });

        const allScores = Array.from(routeScores.values())
          .sort((a, b) => b.points - a.points)
          .slice(0, 6);

        const totalScore = allScores.reduce((sum, score) => sum + score.points, 0);
        return { athlete, totalScore };
      }).sort((a, b) => b.totalScore - a.totalScore);

      // Store ranking position for each athlete (1-based)
      genderRanking.forEach((item, index) => {
        athleteRankings.set(item.athlete.id, index + 1);
      });
    });

    for (let routeNumber = 1; routeNumber <= 40; routeNumber++) {
      const routeAttempts = attempts.filter(a => a.routeNumber === routeNumber);
      const route = ROUTES.find(r => r.number === routeNumber)!;

      const uniqueAthletes = new Set(routeAttempts.map(a => a.athleteId)).size;
      const totalAttempts = routeAttempts.length;

      const completedAttempts = routeAttempts.filter(a => a.result === "completed");
      const flashAttempts = completedAttempts.filter(a => a.pointType === "flash");

      const successfulCompletions = completedAttempts.length;
      const flashCompletions = flashAttempts.length;

      // Get unique athletes who completed this route
      const athletesWhoCompleted = new Set(completedAttempts.map(a => a.athleteId));
      const uniqueCompletions = athletesWhoCompleted.size;

      // Calculate new metrics
      const percentAttempted = totalAthletes > 0 ? (uniqueAthletes / totalAthletes) * 100 : 0;
      const percentCompleted = uniqueAthletes > 0 ? (uniqueCompletions / uniqueAthletes) * 100 : 0;
      const successRate = totalAttempts > 0 ? (successfulCompletions / totalAttempts) * 100 : 0;
      const flashRate = uniqueAthletes > 0 ? (flashCompletions / uniqueAthletes) * 100 : 0;

      // Calculate average attempts to complete (for those who completed)
      let totalAttemptsToComplete = 0;

      athletesWhoCompleted.forEach(athleteId => {
        const athleteAttempts = routeAttempts
          .filter(a => a.athleteId === athleteId)
          .sort((a, b) => a.attemptNumber - b.attemptNumber);

        const firstSuccess = athleteAttempts.find(a => a.result === "completed");
        if (firstSuccess) {
          totalAttemptsToComplete += firstSuccess.attemptNumber;
        }
      });

      const averageAttemptsToComplete = athletesWhoCompleted.size > 0
        ? totalAttemptsToComplete / athletesWhoCompleted.size
        : 0;

      // Calculate average score for completed routes
      const completedScores = completedAttempts.map(a => a.points);
      const averageScoreCompleted = completedScores.length > 0
        ? completedScores.reduce((sum, score) => sum + score, 0) / completedScores.length
        : 0;

      // Calculate average ranking of athletes who completed this route
      const rankingsOfCompletors: number[] = [];
      athletesWhoCompleted.forEach(athleteId => {
        const ranking = athleteRankings.get(athleteId);
        if (ranking) {
          rankingsOfCompletors.push(ranking);
        }
      });

      const averageRankingCompleted = rankingsOfCompletors.length > 0
        ? rankingsOfCompletors.reduce((sum, rank) => sum + rank, 0) / rankingsOfCompletors.length
        : 0;

      // Determine difficulty based on success rate AND minimum attempts
      let difficulty: 'Fácil' | 'Média' | 'Difícil' | 'Ninguém fez';
      const minAthletes = 10;

      if (totalAttempts === 0) {
        difficulty = 'Ninguém fez';
      } else if (uniqueAthletes >= minAthletes && successRate > 70) {
        // Fácil: high success rate AND sufficient athlete volume
        difficulty = 'Fácil';
      } else if (uniqueAthletes >= minAthletes && successRate >= 40) {
        // Média: medium success rate AND sufficient athlete volume
        difficulty = 'Média';
      } else {
        // Difícil: low success rate OR insufficient athlete volume
        difficulty = 'Difícil';
      }

      metrics.push({
        routeNumber,
        totalAttempts,
        uniqueAthletes,
        totalAthletes,
        successfulCompletions,
        flashCompletions,
        percentAttempted,
        percentCompleted,
        successRate,
        flashRate,
        averageAttemptsToComplete,
        averageScoreCompleted,
        averageRankingCompleted,
        flashPoints: route.flashPoints,
        secondPlusPoints: route.secondPlusPoints,
        difficulty,
      });
    }

    return metrics;
  };

  const getSortedAndFilteredRoutes = () => {
    return [...routeMetrics].sort((a, b) => {
      switch (sortBy) {
        case "routeNumber":
          return a.routeNumber - b.routeNumber;
        case "percentAttempted":
          return b.percentAttempted - a.percentAttempted;
        case "successRate":
          return b.successRate - a.successRate;
        case "flashRate":
          return b.flashRate - a.flashRate;
        case "totalAttempts":
          return b.totalAttempts - a.totalAttempts;
        case "averageScore":
          return b.averageScoreCompleted - a.averageScoreCompleted;
        default:
          return a.routeNumber - b.routeNumber;
      }
    });
  };

  const getOverallStats = () => {
    if (routeMetrics.length === 0) return null;

    const routesWithAttempts = routeMetrics.filter(r => r.totalAttempts > 0);
    const avgSuccessRate = routesWithAttempts.length > 0
      ? routesWithAttempts.reduce((sum, r) => sum + r.successRate, 0) / routesWithAttempts.length
      : 0;
    const avgFlashRate = routeMetrics.reduce((sum, r) => sum + r.flashRate, 0) / routeMetrics.length;
    const totalAttempts = routeMetrics.reduce((sum, r) => sum + r.totalAttempts, 0);
    const totalCompletions = routeMetrics.reduce((sum, r) => sum + r.successfulCompletions, 0);

    return {
      avgSuccessRate,
      avgFlashRate,
      totalAttempts,
      totalCompletions,
    };
  };

  const overallStats = getOverallStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dados de performance...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-primary text-white py-6">
          <div className="container mx-auto px-4">
            <Link to="/admin">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar para Admin
              </Button>
            </Link>
          </div>
        </header>
        <main className="container mx-auto px-4 py-8">
          <Card className="text-center py-12 border-red-200 bg-red-50">
            <CardContent>
              <Target className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-800 mb-2">Erro ao Carregar Dados</h3>
              <p className="text-red-700 mb-4">{error}</p>
              <Button onClick={loadRoutePerformanceData} variant="outline">
                Tentar Novamente
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-primary text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-4">
            <Link to="/admin">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar para Admin
              </Button>
            </Link>
            <div className="flex items-center space-x-3">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <Target className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Performance das Vias</h1>
                <p className="text-blue-100">Análise para Route Setters</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        
        {/* Overall Statistics */}
        {overallStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Taxa média de sucesso</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overallStats.avgSuccessRate.toFixed(1)}%</div>
                <p className="text-xs text-muted-foreground">Média de todas as vias</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Taxa de Flash Média</CardTitle>
                <Zap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overallStats.avgFlashRate.toFixed(1)}%</div>
                <p className="text-xs text-muted-foreground">Flash na primeira tentativa</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total de Tentativas</CardTitle>
                <Target className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overallStats.totalAttempts}</div>
                <p className="text-xs text-muted-foreground">Todas as vias</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total de Conclusões</CardTitle>
                <Star className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{overallStats.totalCompletions}</div>
                <p className="text-xs text-muted-foreground">Vias completadas</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters and Sort */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block">Ordenar por:</label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="routeNumber">Número da Via</SelectItem>
                <SelectItem value="percentAttempted">Vias que mais atletas tentaram</SelectItem>
                <SelectItem value="successRate">Taxa de Sucesso</SelectItem>
                <SelectItem value="flashRate">Taxa de Flash</SelectItem>
                <SelectItem value="totalAttempts">Total de Tentativas</SelectItem>
                <SelectItem value="averageScore">Pontuação Média</SelectItem>
              </SelectContent>
            </Select>
          </div>

        </div>

        {/* Route Performance Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {getSortedAndFilteredRoutes().map((route) => (
            <Card key={route.routeNumber} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Via {route.routeNumber}</CardTitle>
                </div>
                <CardDescription>
                  Flash: {route.flashPoints} pts | 2ª+: {route.secondPlusPoints} pts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Tentativas:</span>
                    <div className="font-semibold">{route.totalAttempts}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Atletas:</span>
                    <div className="font-semibold">{route.uniqueAthletes}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Completaram:</span>
                    <div className="font-semibold">{route.successfulCompletions}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Flash:</span>
                    <div className="font-semibold">{route.flashCompletions}</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>% de atletas que tentaram:</span>
                    <span className="font-semibold">{route.percentAttempted.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${Math.min(route.percentAttempted, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>% que completou:</span>
                    <span className="font-semibold">{route.percentCompleted.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${Math.min(route.percentCompleted, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>taxa de sucesso:</span>
                    <span className="font-semibold">{route.successRate.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-purple-500 h-2 rounded-full"
                      style={{ width: `${Math.min(route.successRate, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>taxa de flash:</span>
                    <span className="font-semibold">{route.flashRate.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-500 h-2 rounded-full"
                      style={{ width: `${Math.min(route.flashRate, 100)}%` }}
                    ></div>
                  </div>
                </div>

                {route.averageAttemptsToComplete > 0 && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Tentativas médias para completar:</span>
                    <div className="font-semibold">{route.averageAttemptsToComplete.toFixed(1)}</div>
                  </div>
                )}

                {route.averageScoreCompleted > 0 && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Pontuação média:</span>
                    <div className="font-semibold">{route.averageScoreCompleted.toFixed(0)} pts</div>
                  </div>
                )}

                {route.averageRankingCompleted > 0 && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Ranking médio dos completadores:</span>
                    <div className="font-semibold">#{route.averageRankingCompleted.toFixed(0)}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {getSortedAndFilteredRoutes().length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhuma via encontrada</h3>
              <p className="text-muted-foreground">
                Ajuste os filtros para ver mais resultados.
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 py-8 mt-16">
        <div className="container mx-auto px-4 text-center text-gray-600">
          <p>&copy; 2024 Festival de Escalada - Boulder Academia</p>
        </div>
      </footer>
    </div>
  );
}
